// Function to open chat modal with a specific channel
const openChatWithChannel = (channel) => {
  try {
    if (window.openChatModal && typeof window.openChatModal === 'function') {
      window.openChatModal(channel);
    } else {
      const event = new CustomEvent('openChatModal', {
        detail: { channel, action: 'open' }
      });
      window.dispatchEvent(event);
    }
  } catch (error) {
    console.error('Error opening chat modal with channel:', error);
  }
};

// Function to create a modal to select which channel OR user to share a report view with.
// Now accepts orgUsers so users can share with anyone in their organization,
// not just users with existing chat channels.
export const createStreamChatShareModal = async (filterData, channels, user, chatClient, orgUsers = []) => {
  return new Promise((resolve, reject) => {
    try {
      // Build the channel list HTML
      const channelListHtml = channels.map(channel => {
        let channelName = 'Unnamed Chat';
        if (channel.data?.name) {
          channelName = channel.data.name;
        } else {
          const otherMembers = Object.values(channel.state.members).filter(m => m.user_id !== user.id);
          if (otherMembers.length > 0) {
            channelName = otherMembers.map(m => m.user?.name || m.user_id).join(', ');
          }
        }
        const memberCount = Object.keys(channel.state.members).length;
        return `
          <button
            class="share-item"
            data-channel-id="${channel.id}"
          >
            <div style="display:flex;align-items:center;justify-content:space-between;width:100%">
              <div style="flex:1">
                <p style="font-weight:500;color:var(--lodge-onyx-800);font-size:14px;margin:0">${channelName}</p>
                <p style="font-size:12px;color:var(--lodge-onyx-400);margin:2px 0 0">${memberCount} member${memberCount !== 1 ? 's' : ''}</p>
              </div>
              <svg style="color:var(--lodge-onyx-300)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </button>
        `;
      }).join('');

      // Build the org users list HTML (users without existing chats)
      const existingChatUserIds = new Set();
      channels.forEach(channel => {
        Object.values(channel.state.members).forEach(m => {
          if (m.user_id !== user.id) existingChatUserIds.add(m.user_id);
        });
      });

      // Filter orgUsers to only those without existing 1:1 chats
      const newUsers = orgUsers.filter(u => !existingChatUserIds.has(u.id));

      const newUsersHtml = newUsers.map(u => {
        const initial = (u.name || u.email || 'U').charAt(0).toUpperCase();
        return `
          <button
            class="share-item share-item-user"
            data-user-id="${u.id}"
            data-user-email="${u.email}"
            data-user-name="${u.name}"
          >
            <div style="display:flex;align-items:center;gap:12px;width:100%">
              <div style="width:36px;height:36px;border-radius:8px;background:var(--lodge-orange-400);color:white;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:14px;flex-shrink:0">
                ${initial}
              </div>
              <div style="flex:1;min-width:0">
                <p style="font-weight:500;color:var(--lodge-onyx-800);font-size:14px;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${u.name}</p>
                <p style="font-size:12px;color:var(--lodge-onyx-400);margin:2px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${u.email}</p>
              </div>
              <svg style="color:var(--lodge-onyx-300)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </button>
        `;
      }).join('');

      const modal = document.createElement('div');
      modal.className = 'filter-share-modal-backdrop';
      modal.innerHTML = `
        <div class="filter-share-modal-card">
          <div class="filter-share-modal-header">
            <div style="display:flex;align-items:center;gap:12px">
              <div style="width:40px;height:40px;border-radius:12px;background:var(--lodge-orange-50,#FFF4ED);display:flex;align-items:center;justify-content:center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--lodge-orange-400,#FF7A00)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                  <polyline points="16 6 12 2 8 6"></polyline>
                  <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
              </div>
              <div>
                <h3 style="font-size:17px;font-weight:700;color:var(--lodge-onyx-800,#1A1A1F);letter-spacing:-0.2px;margin:0">Share Report View</h3>
                <p style="font-size:13px;color:var(--lodge-onyx-400,#72727F);margin:2px 0 0">
                  ${filterData.reportName || 'Report'}${filterData.filterCounts?.total > 0 ? ` • ${filterData.filterCounts.total} filter${filterData.filterCounts.total !== 1 ? 's' : ''}` : ''}
                </p>
              </div>
            </div>
            <button id="close-modal" style="width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--lodge-onyx-400,#72727F);border:none;background:transparent;cursor:pointer;transition:background 0.15s">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Search -->
          <div style="padding:12px 24px 0">
            <div style="position:relative">
              <svg style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--lodge-onyx-300,#A0A0AE);pointer-events:none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                id="share-search"
                type="text"
                placeholder="Search conversations or team members..."
                style="width:100%;padding:8px 16px 8px 36px;background:var(--lodge-onyx-50,#F6F6FA);border:1px solid var(--lodge-onyx-100,#E8E8F0);border-radius:12px;font-size:13px;color:var(--lodge-onyx-900,#0F0F11);outline:none;font-family:var(--font-sans,'DM Sans',system-ui,sans-serif);box-sizing:border-box"
              />
            </div>
          </div>

          <div style="padding:16px 24px;overflow-y:auto;max-height:400px" id="share-list">
            ${channels.length > 0 ? `
              <p style="font-size:11px;font-weight:600;color:var(--lodge-onyx-400,#72727F);text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px">
                Existing Conversations
              </p>
              <div id="channel-list" style="margin-bottom:16px">
                ${channelListHtml}
              </div>
            ` : ''}

            ${newUsers.length > 0 ? `
              <p style="font-size:11px;font-weight:600;color:var(--lodge-onyx-400,#72727F);text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px">
                Other Team Members
              </p>
              <div id="user-list">
                ${newUsersHtml}
              </div>
            ` : ''}

            ${channels.length === 0 && newUsers.length === 0 ? `
              <div style="text-align:center;padding:32px 0;color:var(--lodge-onyx-400,#72727F)">
                <p style="font-size:14px;margin:0">No conversations or team members available</p>
                <p style="font-size:12px;margin:4px 0 0">Start a conversation to share report views</p>
              </div>
            ` : ''}
          </div>
        </div>
      `;

      // Inject styles
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        .filter-share-modal-backdrop {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15,15,17,.4);
          backdrop-filter: blur(4px);
          z-index: 100;
        }

        .filter-share-modal-card {
          background: var(--lodge-white, #fff);
          border-radius: var(--radius-xl, 16px);
          box-shadow: var(--shadow-xl, 0 24px 48px -12px rgba(15,15,17,.18));
          max-width: 480px;
          width: calc(100% - 32px);
          max-height: 80vh;
          overflow: hidden;
          animation: filterShareIn 0.3s cubic-bezier(0,0,.2,1) forwards;
          font-family: var(--font-sans, 'DM Sans', system-ui, sans-serif);
        }

        @keyframes filterShareIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .filter-share-modal-header {
          padding: 24px;
          border-bottom: 1px solid var(--lodge-onyx-100, #E8E8F0);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .share-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.15s;
          margin-bottom: 4px;
          text-align: left;
        }

        .share-item:hover {
          background: var(--lodge-onyx-50, #F6F6FA);
          border-color: var(--lodge-onyx-100, #E8E8F0);
        }

        #close-modal:hover {
          background: var(--lodge-onyx-50, #F6F6FA);
        }

        #share-search:focus {
          border-color: var(--lodge-orange-400, #FF7A00);
          box-shadow: 0 0 0 3px rgba(255,122,0,.12);
        }
      `;
      document.head.appendChild(styleElement);
      document.body.appendChild(modal);

      // Search functionality
      const searchInput = modal.querySelector('#share-search');
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.toLowerCase();
        const items = modal.querySelectorAll('.share-item');
        items.forEach(item => {
          const text = item.textContent.toLowerCase();
          item.style.display = text.includes(query) ? '' : 'none';
        });

        // Show/hide section headers based on visible items
        const channelSection = modal.querySelector('#channel-list');
        const userSection = modal.querySelector('#user-list');
        if (channelSection) {
          const visibleChannels = channelSection.querySelectorAll('.share-item:not([style*="display: none"])');
          const header = channelSection.previousElementSibling;
          if (header) header.style.display = visibleChannels.length > 0 ? '' : 'none';
        }
        if (userSection) {
          const visibleUsers = userSection.querySelectorAll('.share-item:not([style*="display: none"])');
          const header = userSection.previousElementSibling;
          if (header) header.style.display = visibleUsers.length > 0 ? '' : 'none';
        }
      });

      const closeModal = () => {
        document.body.removeChild(modal);
        document.head.removeChild(styleElement);
        resolve();
      };

      modal.querySelector('#close-modal').addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

      // Show success toast
      const showSuccess = (recipientName) => {
        const successEl = document.createElement('div');
        successEl.style.cssText = `
          position:fixed;top:24px;right:24px;
          background:var(--lodge-white,#fff);border-left:4px solid var(--color-success,#12B76A);
          box-shadow:var(--shadow-lg,0 12px 24px -4px rgba(15,15,17,.12));
          border-radius:0 12px 12px 0;padding:16px;
          display:flex;align-items:flex-start;gap:12px;
          z-index:200;
          font-family:var(--font-sans,'DM Sans',system-ui,sans-serif);
          animation:filterShareIn 0.3s cubic-bezier(0,0,.2,1) forwards;
        `;
        successEl.innerHTML = `
          <svg style="color:var(--color-success,#12B76A);flex-shrink:0;margin-top:1px" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <div>
            <p style="font-weight:600;color:var(--lodge-onyx-800,#1A1A1F);font-size:14px;margin:0">Shared successfully</p>
            <p style="font-size:13px;color:var(--lodge-onyx-400,#72727F);margin:2px 0 0">Shared with ${recipientName}</p>
          </div>
        `;
        document.body.appendChild(successEl);

        setTimeout(() => {
          successEl.style.opacity = '0';
          successEl.style.transition = 'opacity 0.3s';
          setTimeout(() => { if (document.body.contains(successEl)) document.body.removeChild(successEl); }, 300);
        }, 2000);
      };

      // Handle sending to a channel
      const sendToChannel = async (selectedChannel) => {
        await selectedChannel.sendMessage({
          text: `Shared report view: ${filterData.reportName || 'Report'}`,
          attachments: [{ type: filterData.attachmentType || 'pbi_filter_share', ...filterData }]
        });

        showSuccess(selectedChannel.data?.name || 'recipient');
        closeModal();
        setTimeout(() => openChatWithChannel(selectedChannel), 500);
        resolve(selectedChannel);
      };

      // Handle sending to a NEW user (creates a channel first)
      const sendToUser = async (userId, userName) => {
        if (!chatClient) throw new Error('Chat client not available');

        // Create a new 1:1 channel with the selected user
        const channel = chatClient.channel('messaging', {
          members: [user.id, userId],
          name: `Chat with ${userName}`,
        });

        await channel.create();
        await channel.watch();

        await channel.sendMessage({
          text: `Shared report view: ${filterData.reportName || 'Report'}`,
          attachments: [{ type: filterData.attachmentType || 'pbi_filter_share', ...filterData }]
        });

        showSuccess(userName);
        closeModal();
        setTimeout(() => openChatWithChannel(channel), 500);
        resolve(channel);
      };

      // Handle clicks on channel items
      const shareList = modal.querySelector('#share-list');
      shareList.addEventListener('click', async (e) => {
        const channelButton = e.target.closest('[data-channel-id]');
        const userButton = e.target.closest('[data-user-id]');

        if (channelButton) {
          const channelId = channelButton.dataset.channelId;
          const selectedChannel = channels.find(c => c.id === channelId);
          if (!selectedChannel) return;

          // Show loading
          channelButton.style.opacity = '0.5';
          channelButton.style.pointerEvents = 'none';

          try {
            await sendToChannel(selectedChannel);
          } catch (error) {
            console.error('Error sending filter share message:', error);
            channelButton.style.opacity = '1';
            channelButton.style.pointerEvents = '';
            reject(error);
          }
        } else if (userButton) {
          const userId = userButton.dataset.userId;
          const userName = userButton.dataset.userName;

          // Show loading
          userButton.style.opacity = '0.5';
          userButton.style.pointerEvents = 'none';

          try {
            await sendToUser(userId, userName);
          } catch (error) {
            console.error('Error creating channel and sending filter share:', error);
            userButton.style.opacity = '1';
            userButton.style.pointerEvents = '';
            reject(error);
          }
        }
      });

    } catch (error) {
      console.error('Error creating Stream Chat share modal:', error);
      reject(error);
    }
  });
};
